import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["guideline", "type", "name", "typeLabel", "typeIcon", "typeButton", "dropdown", "status", "saveButton", "artifactOptions", "stylePrompt", "stylePresetButton", "sourceScope", "autoGenerate", "brandName", "group"]
  static values = {
    defaultGuidelines: Object,
    stylePresets: Object,
    saveUrl: String,
    dataUrl: String,
    defaultGroupIds: Array
  }

  connect() {
    this.saveTimeout = null
    this.isSaving = false
    this.boundCloseOnOutsideClick = this.closeOnOutsideClick.bind(this)

    // Store outcomes data in memory for quick switching
    this.outcomesCache = null

    // Listen for activity list changes (delete, reorder, create)
    this.boundHandleActivityListChanged = this.handleActivityListChanged.bind(this)
    window.addEventListener('activity-list-changed', this.boundHandleActivityListChanged)
    this.toggleArtifactOptions(this.hasTypeTarget ? this.typeTarget.value : null)
    this.syncStylePresetButtons()
  }

  disconnect() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
    document.removeEventListener("click", this.boundCloseOnOutsideClick)
    window.removeEventListener('activity-list-changed', this.boundHandleActivityListChanged)
  }

  toggleDropdown(event) {
    event.stopPropagation()

    if (this.hasDropdownTarget) {
      if (this.dropdownTarget.classList.contains("hidden")) {
        this.openDropdown()
      } else {
        this.closeDropdown()
      }
    }
  }

  openDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove("hidden")
      setTimeout(() => {
        document.addEventListener("click", this.boundCloseOnOutsideClick)
      }, 10)
    }
  }

  closeDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden")
      document.removeEventListener("click", this.boundCloseOnOutsideClick)
    }
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown()
    }
  }

  selectType(event) {
    event.stopPropagation()
    const typeValue = event.currentTarget.dataset.typeValue
    const typeLabel = event.currentTarget.dataset.typeLabel
    const typeIcon = event.currentTarget.dataset.typeIcon

    // Update hidden input
    if (this.hasTypeTarget) {
      this.typeTarget.value = typeValue
    }

    // Update button label
    if (this.hasTypeLabelTarget) {
      this.typeLabelTarget.textContent = typeLabel
    }

    // Update button icon
    if (this.hasTypeIconTarget && typeIcon) {
      // Find the icon component and update it
      const iconElement = this.typeIconTarget.querySelector('svg')
      if (iconElement) {
        // Get the new icon by copying from the clicked menu item
        const menuItemIcon = event.currentTarget.querySelector('svg')
        if (menuItemIcon) {
          iconElement.innerHTML = menuItemIcon.innerHTML
        }
      }
    }

    // Update selected state in dropdown
    if (this.hasDropdownTarget) {
      this.dropdownTarget.querySelectorAll('[data-type-value]').forEach(btn => {
        const parent = btn.parentElement
        if (btn.dataset.typeValue === typeValue) {
          parent.classList.add('bg-[#FAFAFB]')
        } else {
          parent.classList.remove('bg-[#FAFAFB]')
        }
      })
    }

    // Close dropdown
    this.closeDropdown()

    this.toggleArtifactOptions(typeValue)

    // Load outcome data for this type
    this.typeLoading = this.loadOutcomeData(typeValue)
  }

  async loadOutcomeData(typeValue) {
    try {
      // Load outcomes cache if not loaded yet
      if (!this.outcomesCache) {
        const response = await fetch(this.hasDataUrlValue ? this.dataUrlValue : this.saveUrlValue.replace('/save_outcomes_config', ''), {
          headers: {
            'Accept': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          // New structure: { "type": [...], "input_activity_ids": [...] }
          const outcomesData = data.outcomes_data
          if (outcomesData && typeof outcomesData === 'object' && Array.isArray(outcomesData.type)) {
            this.outcomesCache = outcomesData.type
          } else if (Array.isArray(outcomesData)) {
            // Old format - still support
            this.outcomesCache = outcomesData
          } else {
            this.outcomesCache = []
          }
        } else {
          this.outcomesCache = []
        }
      }

      if (this.typeTarget.value !== typeValue) return

      // Find outcome for this type
      const outcome = this.outcomesCache.find(o => o.type === typeValue)

      // Get default values
      const defaultName = this.getDefaultName(typeValue)
      if (this.hasGroupTarget) {
        const selected = outcome?.group_ids ?? this.defaultGroupIdsValue
        this.groupTargets.forEach(group => { group.checked = !group.disabled && selected.includes(group.value) })
      }

      // Update form fields with outcome data or defaults
      if (this.hasNameTarget) {
        this.nameTarget.value = outcome?.name || defaultName
      }

      if (this.hasGuidelineTarget) {
        this.guidelineTarget.value = outcome?.guideline ?? this.getDefaultGuideline(typeValue)
      }

      if (this.hasStylePromptTarget) {
        this.stylePromptTarget.value = outcome?.style_prompt || this.stylePromptTarget.dataset.defaultStyle || ''
      }
      this.syncStylePresetButtons()
      if (this.hasSourceScopeTarget) {
        this.sourceScopeTarget.value = outcome?.source_scope || 'aggregate'
      }
      if (this.hasAutoGenerateTarget) {
        this.autoGenerateTarget.checked = outcome?.auto_generate === true || outcome?.auto_generate === 'true'
      }
      if (this.hasBrandNameTarget) {
        this.brandNameTarget.value = outcome?.brand_name || ''
      }
      this.toggleArtifactOptions(typeValue)
    } catch (error) {
      console.error('Error loading outcome data:', error)
      if (this.typeTarget.value !== typeValue) return
      // If error, still show default values
      if (this.hasNameTarget) {
        this.nameTarget.value = this.getDefaultName(typeValue)
      }
      if (this.hasGuidelineTarget) {
        this.guidelineTarget.value = this.getDefaultGuideline(typeValue)
      }
    }
  }

  getDefaultGuideline(typeValue) {
    const guideline = this.defaultGuidelinesValue[typeValue] || ''
    return this.hasDefaultGroupIdsValue ? guideline.replace(/participant/gi, 'group') : guideline
  }

  getDefaultName(typeValue) {
    const defaults = {
      'individual_insights': 'Quick Insights',
      'aggregate_insights': 'Aggregate Insights',
      'artifact': 'Custom Artifact',
      'document': 'Document',
      'summary': 'Summary'
    }
    return defaults[typeValue] || typeValue
  }

  applyStylePreset(event) {
    event.preventDefault()
    if (!this.hasStylePromptTarget) return

    const presetPrompt = this.stylePresets()?.[event.currentTarget.dataset.presetKey]
    if (!presetPrompt) return

    this.stylePromptTarget.value = presetPrompt
    this.syncStylePresetButtons()
  }

  syncStylePresetButtons() {
    if (!this.hasStylePresetButtonTarget) return

    const activeClasses = ['border-[#7147FF]', 'bg-[#EBEBFF]', 'text-[#4427BF]']
    const inactiveClasses = ['border-[#E4E5E8]', 'bg-white', 'text-[#6B7280]']
    const current = (this.hasStylePromptTarget ? this.stylePromptTarget.value : '').trim()

    this.stylePresetButtonTargets.forEach((button) => {
      const presetPrompt = (this.stylePresets()?.[button.dataset.presetKey] || '').trim()
      const isActive = current.length > 0 && current === presetPrompt

      button.setAttribute('aria-pressed', isActive.toString())
      activeClasses.forEach((klass) => button.classList.toggle(klass, isActive))
      inactiveClasses.forEach((klass) => button.classList.toggle(klass, !isActive))
    })
  }

  stylePresets() {
    return this.hasStylePresetsValue ? this.stylePresetsValue : {}
  }

  toggleArtifactOptions(typeValue) {
    if (!this.hasArtifactOptionsTarget) return

    const show = typeValue === 'artifact'
    this.artifactOptionsTarget.classList.toggle('hidden', !show)
    this.artifactOptionsTarget.classList.toggle('flex', show)
  }

  scheduleUpdate() {
    // No auto-save, just enable the save button
    // User must click Save button manually
  }

  async save() {
    if (this.isSaving) return false
    this.isSaving = true

    try {
      if (this.typeLoading) await this.typeLoading
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const formData = new FormData()

      if (this.hasGuidelineTarget) {
        formData.append('outcomes[guideline]', this.guidelineTarget.value)
      }

      if (this.hasTypeTarget) {
        formData.append('outcomes[type]', this.typeTarget.value || 'document')
      }

      if (this.hasNameTarget) {
        formData.append('outcomes[name]', this.nameTarget.value)
      }
      if (this.hasDefaultGroupIdsValue) {
        const selected = this.groupTargets.filter(group => group.checked && !group.disabled)
        if (!selected.length) formData.append('outcomes[group_ids][]', '')
        selected.forEach(group => formData.append('outcomes[group_ids][]', group.value))
      }

      if (this.hasTypeTarget && this.typeTarget.value === 'artifact') {
        if (this.hasStylePromptTarget) {
          formData.append('outcomes[style_prompt]', this.stylePromptTarget.value)
        }
        if (this.hasSourceScopeTarget) {
          formData.append('outcomes[source_scope]', this.sourceScopeTarget.value)
        }
        if (this.hasAutoGenerateTarget) {
          formData.append('outcomes[auto_generate]', this.autoGenerateTarget.checked ? 'true' : 'false')
        }
        if (this.hasBrandNameTarget) {
          formData.append('outcomes[brand_name]', this.brandNameTarget.value)
        }
      }

      // For document validation template, collect input_activity_ids from file-upload controller
      const fileUploadController = this.application.getControllerForElementAndIdentifier(
        this.element.querySelector('[data-controller*="file-upload"]'),
        'file-upload'
      )

      if (fileUploadController && fileUploadController.activityChips) {
        const activityIds = fileUploadController.activityChips.map(chip => chip.id)
        if (this.hasDefaultGroupIdsValue && !activityIds.length) formData.append('outcomes[input_activity_ids][]', '')
        activityIds.forEach(id => {
          formData.append('outcomes[input_activity_ids][]', id)
        })
      }

      // Collect pending file uploads from file-upload controller
      if (fileUploadController) {
        const files = fileUploadController.getFiles()
        files.forEach(file => {
          formData.append('outcome_files[]', file, file.name)
        })
      }

      const response = await fetch(this.saveUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: formData
      })

      if (response.ok) {
        const data = await response.json()
        this.updateSaveButton("Saved")

        // Transition uploaded files to persisted chips (no page reload)
        if (data.persisted_files && data.persisted_files.length > 0 && fileUploadController) {
          fileUploadController.transitionToPersistedFiles(data.persisted_files)
        }

        // Update cache with saved data - new structure: { "type": [...], "input_activity_ids": [...] }
        const outcomesData = data.outcomes
        if (outcomesData && typeof outcomesData === 'object' && Array.isArray(outcomesData.type)) {
          this.outcomesCache = outcomesData.type
        } else if (Array.isArray(outcomesData)) {
          // Old format - still support
          this.outcomesCache = outcomesData
        } else {
          this.outcomesCache = []
        }

        // Clear status after 2 seconds
        setTimeout(() => {
          this.updateSaveButton("Save")
        }, 2000)
        this.updateStatus('')
        return true
      } else {
        const data = await response.json().catch(() => ({}))
        this.updateStatus(data.error || 'Unable to save outcome configuration. Try again.')
        this.updateSaveButton("Error")
        setTimeout(() => {
          this.updateSaveButton("Save")
        }, 2000)
      }
    } catch (error) {
      console.error('Error saving outcomes config:', error)
      this.updateStatus('Unable to save outcome configuration. Try again.')
      this.updateSaveButton("Error")
      setTimeout(() => {
        this.updateSaveButton("Save")
      }, 2000)
    } finally {
      this.isSaving = false
    }
    return false
  }

  cancel() {
    // Clear any pending saves
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }

    // Reload the page to reset form
    window.location.reload()
  }

  updateStatus(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
    }
  }

  updateSaveButton(text) {
    if (this.hasSaveButtonTarget) {
      // Store original button content on first call
      if (!this.originalButtonContent) {
        this.originalButtonContent = this.saveButtonTarget.innerHTML
      }

      if (text === "Save") {
        // Restore original button content with icon
        this.saveButtonTarget.innerHTML = this.originalButtonContent
      } else {
        // For "Saved" or "Error", show text only without icon
        this.saveButtonTarget.textContent = text
      }
    }
  }

  async handleActivityListChanged() {
    // When activity list changes (delete, reorder, create), update activity chips
    // to ensure input_activity_ids are synced with the backend

    // The backend has already synced input_activity_ids via:
    // - cleanup_deleted_activity_references (for delete)
    // - sync_input_activity_ids_after_reorder (for reorder)
    // - initialize_activity_outcomes (for create)

    // Fetch fresh activity data from backend
    await this.refreshInputActivities()
  }

  async refreshInputActivities() {
    try {
      // Fetch the activity data to get updated input_activity_ids
      const response = await fetch(this.hasDataUrlValue ? this.dataUrlValue : this.saveUrlValue.replace('/save_outcomes_config', ''), {
        headers: {
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        console.error('Failed to fetch activity data')
        return
      }

      const data = await response.json()
      const outcomesData = data.outcomes_data

      // Get updated input_activity_ids
      let inputActivityIds = []
      if (outcomesData && typeof outcomesData === 'object' && Array.isArray(outcomesData.input_activity_ids)) {
        inputActivityIds = outcomesData.input_activity_ids
      }

      // Find the file-upload controller and update its activity chips
      const fileUploadController = this.application.getControllerForElementAndIdentifier(
        this.element.querySelector('[data-controller*="file-upload"]'),
        'file-upload'
      )

      if (fileUploadController) {
        // Clear existing activity chips
        fileUploadController.activityChips = []
        const fileListElement = fileUploadController.fileListTarget
        const existingChips = fileListElement.querySelectorAll('[data-activity-chip]')
        existingChips.forEach(chip => chip.remove())

        // Fetch fresh activity data for the input_activity_ids
        if (inputActivityIds.length > 0) {
          const activitiesData = await this.fetchActivitiesData(inputActivityIds)

          // Re-render activity chips
          activitiesData.forEach(activity => {
            fileUploadController.addActivityChip(activity)
          })
        }
      }
    } catch (error) {
      console.error('Error refreshing input activities:', error)
    }
  }

  async fetchActivitiesData(activityIds) {
    // Build activities data from DOM instead of fetching from backend
    // This is more reliable and faster than making an API call
    const activities = []

    // Check if this is a single phase inloop
    const allPhases = document.querySelectorAll('[data-phase-id]')
    const isSinglePhase = allPhases.length <= 1

    // Get all activity cards from the DOM
    const activityCards = document.querySelectorAll('[data-activity-card]')

    activityCards.forEach(card => {
      const activityId = card.dataset.activityId

      // Only include activities that are in the input_activity_ids list
      if (!activityIds.includes(activityId)) {
        return
      }

      const activityNumberElement = card.querySelector('[data-activity-number]')
      const activityNumber = activityNumberElement ? parseInt(activityNumberElement.textContent) : null
      const activityTitle = card.dataset.activityTitle
      const activityType = card.dataset.activityType
      const activityBadge = card.dataset.activityBadge

      // Get phase number if multi-phase
      let phaseNumber = null
      if (!isSinglePhase) {
        const phaseContainer = card.closest('[data-phase-id]')
        if (phaseContainer) {
          const allPhasesArray = Array.from(allPhases)
          phaseNumber = allPhasesArray.indexOf(phaseContainer) + 1
        }
      }

      activities.push({
        id: activityId,
        title: activityTitle,
        activityNumber: activityNumber,
        type: activityType?.replace('_template', '') || 'ai_conversation',
        badge: activityBadge || 'ai_agent',
        phaseNumber: phaseNumber
      })
    })

    return activities
  }
}
